// OSIS - Gestionare Materiale - Stocuri profile, Materiale, materiale
import React, { useState } from 'react';
import { 
  Package, Search, AlertTriangle, TrendingDown, TrendingUp, Plus,
  Truck, BarChart, Boxes, ShoppingCart, Clock, CheckCircle
} from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import { mockData } from '../../utils/mockData';

const MaterialsManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  // Inventar materiale OSIS extins
  const materials = [
    ...mockData.inventory,
    // Materiale suplimentare specifice OSIS
    {
      id: 7,
      product: 'rășini epoxidice mahon (ml)',
      quantity: 220,
      minStock: 150,
      status: 'În stoc',
      location: 'Depozit materiale',
      category: 'Profile',
      price: '28 RON/ml',
      supplier: 'Sika Romania',
      lastOrder: '2024-03-10',
      consumption: '45 ml/săptămână'
    },
    {
      id: 8,
      product: 'rășini epoxidice stejar (ml)',
      quantity: 180,
      minStock: 120,
      status: 'În stoc',
      location: 'Depozit materiale',
      category: 'Profile',
      price: '30 RON/ml',
      supplier: 'Sika Romania',
      lastOrder: '2024-03-12',
      consumption: '35 ml/săptămână'
    },
    {
      id: 9,
      product: 'Geam tripan Low-E (mp)',
      quantity: 8,
      minStock: 15,
      status: 'Stoc critic',
      location: 'Depozit Materiale',
      category: 'Materiale',
      price: '75 RON/mp',
      supplier: 'Mapei',
      lastOrder: '2024-03-05',
      consumption: '12 mp/săptămână'
    },
    {
      id: 10,
      product: 'materiale Blastrac (set)',
      quantity: 25,
      minStock: 20,
      status: 'În stoc',
      location: 'Magazie accesorii',
      category: 'materiale',
      price: '95 RON/set',
      supplier: 'Blastrac Romania',
      lastOrder: '2024-03-08',
      consumption: '8 seturi/săptămână'
    },
    {
      id: 11,
      product: 'Silicoane etanșare (tub)',
      quantity: 48,
      minStock: 30,
      status: 'În stoc',
      location: 'Dulap chimicale',
      category: 'Consumabile',
      price: '18 RON/tub',
      supplier: 'Soudal Romania',
      lastOrder: '2024-03-14',
      consumption: '15 tuburi/săptămână'
    },
    {
      id: 12,
      product: 'Spumă poliuretanică (bucată)',
      quantity: 12,
      minStock: 25,
      status: 'Stoc redus',
      location: 'Dulap chimicale',
      category: 'Consumabile',
      price: '15 RON/buc',
      supplier: 'Soudal Romania',
      lastOrder: '2024-03-01',
      consumption: '20 bucăți/săptămână'
    },
    {
      id: 13,
      product: 'Izolație termică (mp)',
      quantity: 65,
      minStock: 40,
      status: 'În stoc',
      location: 'Depozit izolații',
      category: 'Izolații',
      price: '22 RON/mp',
      supplier: 'Knauf Insulation',
      lastOrder: '2024-03-11',
      consumption: '25 mp/săptămână'
    },
    {
      id: 14,
      product: 'Membrane de vapori (role)',
      quantity: 3,
      minStock: 8,
      status: 'Stoc critic',
      location: 'Depozit izolații',
      category: 'Izolații',
      price: '145 RON/rolă',
      supplier: 'Tyvek Romania',
      lastOrder: '2024-02-28',
      consumption: '2 role/săptămână'
    }
  ];

  // Filtrare materiale
  const filteredMaterials = materials.filter(material => {
    const matchesSearch = material.product.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         material.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || material.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Statistici
  const stats = {
    totalItems: materials.length,
    inStock: materials.filter(m => m.status === 'În stoc').length,
    lowStock: materials.filter(m => m.status === 'Stoc redus').length,
    criticalStock: materials.filter(m => m.status === 'Stoc critic').length,
    totalValue: materials.reduce((sum, m) => sum + (m.quantity * parseFloat(m.price.split(' ')[0])), 0)
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'În stoc': return 'success';
      case 'Stoc redus': return 'warning';
      case 'Stoc critic': return 'error';
      default: return 'gray';
    }
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'Profile': return Package;
      case 'Materiale': return Boxes;
      case 'materiale': return Package;
      case 'Garnituri': return Package;
      case 'Mânere': return Package;
      case 'Fixare': return Package;
      case 'Consumabile': return ShoppingCart;
      case 'Izolații': return Package;
      default: return Package;
    }
  };

  const handleOrderMaterial = (material) => {
    setSelectedMaterial(material);
    setIsOrderModalOpen(true);
  };

  const tableColumns = [
    {
      header: 'Material',
      accessor: 'product',
      cell: (row) => {
        const IconComponent = getCategoryIcon(row.category);
        return (
          <div className="flex items-center space-x-3">
            <IconComponent className="w-5 h-5 text-gray-400" />
            <div>
              <p className="font-medium text-gray-900 dark:text-white">{row.product}</p>
              <p className="text-sm text-gray-500">{row.category}</p>
            </div>
          </div>
        );
      },
    },
    {
      header: 'Stoc',
      accessor: 'quantity',
      cell: (row) => (
        <div className="text-center">
          <p className="font-semibold text-gray-900 dark:text-white">{row.quantity}</p>
          <p className="text-xs text-gray-500">Min: {row.minStock}</p>
        </div>
      ),
    },
    {
      header: 'Status',
      accessor: 'status',
      cell: (row) => (
        <div>
          <Badge variant={getStatusColor(row.status)}>
            {row.status}
          </Badge>
          {row.quantity <= row.minStock && (
            <div className="flex items-center mt-1">
              <AlertTriangle className="w-3 h-3 text-red-500 mr-1" />
              <span className="text-xs text-red-600">Necesar comandă</span>
            </div>
          )}
        </div>
      ),
    },
    {
      header: 'Preț',
      accessor: 'price',
      cell: (row) => (
        <p className="font-medium text-gray-900 dark:text-white">{row.price}</p>
      ),
    },
    {
      header: 'Furnizor',
      accessor: 'supplier',
      cell: (row) => (
        <div>
          <p className="text-sm font-medium">{row.supplier}</p>
          <p className="text-xs text-gray-500">
            Ultima comandă: {new Date(row.lastOrder).toLocaleDateString('ro-RO')}
          </p>
        </div>
      ),
    },
    {
      header: 'Consum',
      accessor: 'consumption',
      cell: (row) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {row.consumption}
        </div>
      ),
    },
    {
      header: 'Locație',
      accessor: 'location',
      cell: (row) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {row.location}
        </div>
      ),
    },
    {
      header: 'Acțiuni',
      accessor: 'actions',
      cell: (row) => (
        <button
          onClick={() => handleOrderMaterial(row)}
          className={`btn-sm ${
            row.quantity <= row.minStock 
              ? 'bg-red-100 text-red-700 hover:bg-red-200' 
              : 'btn-ghost'
          }`}
        >
          <ShoppingCart className="w-4 h-4 mr-1" />
          Comandă
        </button>
      ),
    },
  ];

  const categories = [...new Set(materials.map(m => m.category))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Package className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Gestionare Materiale OSIS
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Inventar rășini epoxidice, Materiale, materiale și consumabile
            </p>
          </div>
        </div>
        
        <button className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Adaugă Material
        </button>
      </div>

      {/* Statistici */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Materiale</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalItems}</p>
            </div>
            <Boxes className="w-8 h-8 text-primary-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">În Stoc</p>
              <p className="text-2xl font-bold text-green-600">{stats.inStock}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Stoc Redus</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.lowStock}</p>
            </div>
            <TrendingDown className="w-8 h-8 text-yellow-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Stoc Critic</p>
              <p className="text-2xl font-bold text-red-600">{stats.criticalStock}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Valoare Stoc</p>
              <p className="text-lg font-bold text-primary-600">{Math.round(stats.totalValue).toLocaleString()} RON</p>
            </div>
            <BarChart className="w-8 h-8 text-primary-600" />
          </div>
        </Card>
      </div>

      {/* Alerte Stoc */}
      {(stats.lowStock > 0 || stats.criticalStock > 0) && (
        <Card>
          <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-800 dark:text-red-200">
                Alertă Stoc - Materiale cu stoc redus sau critic
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                {materials.filter(m => m.quantity <= m.minStock).map(material => (
                  <div key={material.id} className="flex items-center justify-between p-3 bg-white dark:bg-red-800/20 rounded-lg">
                    <div>
                      <p className="font-medium text-red-900 dark:text-red-100 text-sm">
                        {material.product}
                      </p>
                      <p className="text-red-700 dark:text-red-300 text-xs">
                        Stoc: {material.quantity} | Min: {material.minStock}
                      </p>
                    </div>
                    <button 
                      onClick={() => handleOrderMaterial(material)}
                      className="btn-sm bg-red-600 hover:bg-red-700 text-white"
                    >
                      <ShoppingCart className="w-3 h-3 mr-1" />
                      Comandă
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Filtre și Căutare */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Căutare material sau categorie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="form-input"
            >
              <option value="all">Toate categoriile</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Tabel Materiale */}
      <Card title="Inventar Materiale" icon={Package}>
        <div className="overflow-x-auto">
          <Table 
            columns={tableColumns} 
            data={filteredMaterials}
          />
        </div>
      </Card>

      {/* Modal Comandă Material */}
      <Modal
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        title={`Comandă Material - ${selectedMaterial?.product || ''}`}
        size="md"
      >
        {selectedMaterial && (
          <div className="space-y-6">
            {/* Informații material */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Material</p>
                  <p className="font-semibold">{selectedMaterial.product}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Categorie</p>
                  <p className="font-semibold">{selectedMaterial.category}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Stoc curent</p>
                  <p className={`font-semibold ${
                    selectedMaterial.quantity <= selectedMaterial.minStock 
                      ? 'text-red-600' 
                      : 'text-green-600'
                  }`}>
                    {selectedMaterial.quantity}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Stoc minim</p>
                  <p className="font-semibold">{selectedMaterial.minStock}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Preț unitar</p>
                  <p className="font-semibold">{selectedMaterial.price}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Furnizor</p>
                  <p className="font-semibold">{selectedMaterial.supplier}</p>
                </div>
              </div>
            </div>

            {/* Formular comandă */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Cantitate de comandat</label>
                <input 
                  type="number" 
                  className="form-input"
                  placeholder="Ex: 100"
                  min="1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Recomandat: {Math.max(selectedMaterial.minStock - selectedMaterial.quantity + 50, 50)} 
                  (pentru 2-3 săptămâni)
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Prioritate comandă</label>
                <select className="form-input">
                  <option value="normal">Normal (5-7 zile)</option>
                  <option value="urgent">Urgent (2-3 zile)</option>
                  <option value="express">Express (24-48 ore)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Observații (opțional)</label>
                <textarea 
                  className="form-input"
                  rows="3"
                  placeholder="Specificații suplimentare pentru comandă..."
                />
              </div>
            </div>

            {/* Estimare cost */}
            <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Cost estimat:</span>
                <span className="text-lg font-bold text-primary-600">
                  {(parseFloat(selectedMaterial.price.split(' ')[0]) * 100).toLocaleString()} RON
                </span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Calculat pentru 100 unități la prețul {selectedMaterial.price}
              </p>
            </div>

            {/* Alertă dacă stoc critic */}
            {selectedMaterial.status === 'Stoc critic' && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800 dark:text-red-200">
                    Stoc critic - Comandă urgentă necesară!
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    Materialul este sub pragul minim și poate afecta producția.
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={() => setIsOrderModalOpen(false)}
                className="btn-secondary"
              >
                Anulează
              </button>
              <button className="btn-primary flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Trimite Comanda
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MaterialsManagement;